/**
 * "Why this price?" comps panel.
 *
 * Renders the eBay sold-listing comps that informed the AI price estimate
 * (persisted by the analysis pipeline under aiAnalysis._marketComps) so the
 * user can judge the estimate instead of trusting a bare number. When the
 * comps look wrong, the existing Retry action re-runs the analysis with
 * fresh comps.
 *
 * @module portal/modules/compsPanel
 */

import type { Lot } from '../types';
import { escapeHtml, escapeAttr, formatCurrencyDisplay } from './formatting';

export interface MarketCompsView {
  query?: string;
  count?: number;
  trimmedMedian?: number;
  trimmedCount?: number;
  average?: number;
  min?: number;
  max?: number;
  fetchedAt?: string;
  topProducts?: Array<{
    title?: string;
    price?: number | null;
    url?: string;
    condition?: string;
    dateSold?: string;
  }>;
}

/** Extract persisted comps from a lot's AI analysis, or null when absent. */
export function getLotMarketComps(lot: Lot | null | undefined): MarketCompsView | null {
  const analysis = lot?.aiAnalysis;
  if (!analysis || typeof analysis !== 'object') return null;
  const comps = (analysis as Record<string, unknown>)._marketComps;
  if (!comps || typeof comps !== 'object') return null;
  return comps as MarketCompsView;
}

function money(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return formatCurrencyDisplay(value) ?? '—';
}

function formatFetchedAt(iso: string | undefined): string {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return '';
  try {
    return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/**
 * Build the comps panel HTML. Returns '' when the lot has no persisted comps
 * (callers hide the container).
 */
export function buildCompsPanelHtml(lot: Lot | null | undefined): string {
  const comps = getLotMarketComps(lot);
  if (!comps) return '';

  const stats: string[] = [];
  if (typeof comps.trimmedMedian === 'number') {
    stats.push(`Median <strong>${money(comps.trimmedMedian)}</strong>`);
  }
  if (typeof comps.min === 'number' && typeof comps.max === 'number') {
    stats.push(`Range ${money(comps.min)}–${money(comps.max)}`);
  }
  if (typeof comps.count === 'number') {
    stats.push(`${comps.count} sold`);
  }
  const fetched = formatFetchedAt(comps.fetchedAt);
  if (fetched) {
    stats.push(`as of ${escapeHtml(fetched)}`);
  }

  const queryLine = comps.query
    ? `<div class="comps-panel__query">Searched eBay sold listings for “${escapeHtml(comps.query)}”</div>`
    : '';

  const products = Array.isArray(comps.topProducts) ? comps.topProducts : [];
  const items = products
    .filter(p => p && typeof p.title === 'string' && p.title)
    .slice(0, 8)
    .map(p => {
      const price = money(p.price ?? null);
      const sold = p.dateSold ? ` · ${escapeHtml(String(p.dateSold))}` : '';
      const condition = p.condition ? ` · ${escapeHtml(String(p.condition))}` : '';
      const titleHtml =
        typeof p.url === 'string' && /^https?:\/\//i.test(p.url)
          ? `<a href="${escapeAttr(p.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(p.title!)}</a>`
          : escapeHtml(p.title!);
      return (
        `<li class="comps-panel__item">` +
        `<span class="comps-panel__item-title">${titleHtml}</span>` +
        `<span class="comps-panel__item-meta">${price}${sold}${condition}</span>` +
        `</li>`
      );
    })
    .join('');

  const list = items
    ? `<ul class="comps-panel__list">${items}</ul>`
    : '<div class="comps-panel__empty">Individual listings unavailable for this analysis.</div>';

  return (
    `<details class="comps-panel">` +
    `<summary class="comps-panel__summary">Why this price? <span class="comps-panel__stats">${stats.join(' · ')}</span></summary>` +
    queryLine +
    list +
    `<div class="comps-panel__hint">Comps look wrong? Use 👎 Retry on the lot to re-run the analysis with fresh comps (1 credit).</div>` +
    `</details>`
  );
}

/** Render the comps panel into a container element (hides it when empty). */
export function renderCompsPanel(container: HTMLElement | null, lot: Lot | null | undefined): void {
  if (!container) return;
  const html = buildCompsPanelHtml(lot);
  container.innerHTML = html;
  container.hidden = !html;
}
